// src/components/LandingMarketTable.js

import React, { useMemo } from 'react';
import './LandingMarketTable.css';

export function formatNumber(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  return n.toLocaleString();
}

function toNum(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export default function LandingMarketTable({ marketFilter = 'largest', rows = [] }) {

  const displayedRows = useMemo(() => {
    const copy = [...rows];

    if (marketFilter === 'winners') {
      copy.sort((a, b) => (toNum(b.changePercent) ?? -Infinity) - (toNum(a.changePercent) ?? -Infinity));
      return copy.slice(0, 20);
    }

    if (marketFilter === 'losers') {
      copy.sort((a, b) => (toNum(a.changePercent) ?? Infinity) - (toNum(b.changePercent) ?? Infinity));
      return copy.slice(0, 20);
    }

    // default: largest companies by market cap
    copy.sort((a, b) => (toNum(b.marketCap) ?? -Infinity) - (toNum(a.marketCap) ?? -Infinity));
    return copy.slice(0, 20);
  }, [rows, marketFilter]);

  return (
    <div className="landing-market-table">
      <table>
        <thead>
          <tr>
            <th>Stock</th>
            <th>Price</th>
            <th>Change</th>
            <th>Market Cap (est.)</th>
            <th>Volume</th>
          </tr>
        </thead>
        <tbody>
          {displayedRows.map((row) => {
            const price = toNum(row.price);
            const changePercent = toNum(row.changePercent);

            return (
              <tr key={row.symbol}>
                <td className="symbol-cell">
                  <strong>{row.symbol}</strong>
                  <span>{row.name}</span>
                  {price == null ? <span>Quote unavailable</span> : row.priceIsStale
                    ? <span>Stale · Close {row.priceAsOf}</span>
                    : row.priceAsOf && <span>Close {row.priceAsOf}</span>}
                </td>

                <td>{price == null ? '—' : `$${price.toFixed(2)}`}</td>

                <td className={changePercent == null ? undefined : changePercent >= 0 ? 'pos' : 'neg'}>
                  {changePercent == null ? '—' : `${changePercent.toFixed(2)}%`}
                </td>

                <td title={row.marketCap == null
                  ? 'Market cap unavailable'
                  : `Estimated using SEC shares as of ${row.sharesOutstandingAsOf || 'unknown'} and close as of ${row.priceAsOf || 'unknown'}`}>
                  {formatNumber(row.marketCap)}
                  {row.sharesIsStale && row.marketCap != null && <span className="landing-market-stale">Older share count</span>}
                </td>
                <td>{formatNumber(row.volume)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
